<template>
  <div class="gs-portal-auth">
    <section class="gs-portal-auth__card gs-refresh-controls">
      <header class="gs-portal-auth__head">
        <p class="gs-portal-auth__eyebrow">Affiliate portal</p>
        <h1 class="gs-portal-auth__title">{{ loginTab === 'signup' ? 'Create your free account' : 'Access your affiliate tools' }}</h1>
      </header>

      <p v-if="loginTab === 'login'" class="gs-portal-auth__intro">Log in to access your links and promotional tools for this affiliate program.</p>

      <!-- Login Form -->
      <div v-if="loginTab === 'login' && isEmbedded" class="gs-portal-auth__form">
        <p v-if="accountCreated" role="status">Your account is created. Log in to finish connecting your affiliate tools.</p>
        <button class="gs-portal-auth__open" type="button" :disabled="signInPending" @click="signInEmbedded">
          {{ signInPending ? 'Waiting for sign-in…' : 'Log in to your Groove account' }}
        </button>
        <p role="status">{{ signInPending ? 'Finish signing in in the window that opened. Your affiliate tools will appear here automatically.' : 'A secure sign-in window opens. After login, you return to these affiliate tools.' }}</p>
        <p v-if="loginError" class="gs-portal-auth__error" role="alert">{{ loginError }}</p>
      </div>
      <div v-else-if="loginTab === 'login'" class="gs-portal-auth__form">
        <p>Sign in securely to get your links for this affiliate program.</p>

        <p v-if="loginError" class="gs-portal-auth__error">{{ loginError }}</p>

        <div class="gs-portal-auth__actions">
          <gs-button variant="primary" @click="login()">
            Log in to your Groove account
          </gs-button>
          <a class="gs-portal-auth__link" :href="forgotPasswordUrl" target="_blank">
            {{ $t('frontend_resource_promo_forgot_password') }}
          </a>
        </div>
      </div>

      <!-- Signup Form -->
      <div v-else-if="loginTab === 'signup'" class="gs-portal-auth__form">
        <p>Create a free Groove account. We’ll sign you in and open this program’s affiliate tools here.</p>
        <div class="gs-portal-auth__row">
          <gs-field :label="$t('frontend_resource_promo_first_name')">
            <text-input
              v-model="localSignupForm.first_name"
              :validator="$v.localSignupForm.first_name"
              :attribute="$t('frontend_resource_promo_first_name')"
              value="localSignupForm.first_name"
              :placeholder="$t('frontend_resource_promo_first_name')"
            />
          </gs-field>

          <gs-field :label="$t('frontend_resource_promo_last_name')">
            <text-input
              v-model="localSignupForm.last_name"
              :validator="$v.localSignupForm.last_name"
              :attribute="$t('frontend_resource_promo_first_name')"
              value="localSignupForm.last_name"
              :placeholder="$t('frontend_resource_promo_last_name')"
            />
          </gs-field>
        </div>

        <gs-field :label="$t('frontend_resource_promo_email')">
          <text-input
            v-model="localSignupForm.email"
            :validator="$v.localSignupForm.email"
            :attribute="$t('frontend_email')"
            value="localSignupForm.email"
            :placeholder="$t('frontend_resource_promo_email')"
          />
        </gs-field>

        <div class="gs-portal-auth__row">
          <gs-field :label="$t('frontend_resource_promo_company_name')">
            <text-input
              v-model="localSignupForm.company_name"
              :validator="$v.localSignupForm.company_name"
              :attribute="$t('frontend_resource_promo_company_name')"
              value="localSignupForm.company_name"
              :placeholder="$t('frontend_resource_promo_company_name')"
            />
          </gs-field>

          <gs-field :label="$t('frontend_resource_promo_username')">
            <text-input
              v-model="localSignupForm.username"
              :validator="$v.localSignupForm.username"
              :attribute="$t('frontend_resource_promo_username')"
              value="localSignupForm.username"
              :placeholder="$t('frontend_resource_promo_username')"
              @input="delayTouch($v.localSignupForm.username)"
            />
          </gs-field>
        </div>

        <div class="gs-portal-auth__row">
          <gs-field :label="$t('frontend_resource_promo_password')">
            <text-input
              v-model="localSignupForm.password"
              :validator="$v.localSignupForm.password"
              :attribute="$t('frontend_resource_promo_password')"
              value="localSignupForm.password"
              :placeholder="$t('frontend_resource_promo_password')"
              type="password"
            />
          </gs-field>

          <gs-field :label="$t('frontend_resource_promo_confirm_password')">
            <text-input
              v-model="localSignupForm.password_confirmation"
              :validator="$v.localSignupForm.password_confirmation"
              :attribute="$t('frontend_resource_promo_password_confirmation')"
              value="localSignupForm.password_confirmation"
              :placeholder="$t('frontend_resource_promo_password_confirmation')"
              type="password"
            />
          </gs-field>
        </div>

        <p v-if="registrationError" class="gs-portal-auth__error">{{ registrationError }}</p>

        <div class="gs-portal-auth__actions">
          <gs-button variant="primary" :disabled="registering || $v.localSignupForm.$pending" @click="registerUser()">
            {{ registering ? 'Creating your account…' : 'Create free Groove account' }}
          </gs-button>
        </div>
      </div>
      <p v-if="loginTab === 'login'" class="gs-portal-auth__switch">
        Don’t have an account?
        <button type="button" class="gs-portal-auth__link" data-tab="signup" @click="handleLoginTabChange('signup')">Create a free account</button>
      </p>
      <p v-else class="gs-portal-auth__switch">
        Already have an account?
        <button type="button" class="gs-portal-auth__link" data-tab="login" @click="handleLoginTabChange('login')">Log in to Groove</button>
      </p>
    </section>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import auth from '@groovepages/gd-lib/auth';
import axios from 'axios';
import {
  required,
  minLength,
  maxLength,
  alphaNum,
  email,
  sameAs,
} from 'vuelidate/lib/validators';
import keycloakService from '@/services/keycloakAuth';
import { openEmbeddedSignIn } from '@/services/portal-embed-bridge';
import { setEmbeddedUser } from '@/services/portal-embed-session';

const touchMap = new WeakMap();

export default {
  props: {
    funnelId: {
      type: Number,
      required: true,
    },
    signupForm: {
      type: Object,
      required: true,
    },
    headerText: {
      type: String,
      required: true,
    },
    loginTabs: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localSignupForm: this.signupForm,
      loginTab: 'login',
      isEmbedded: window.self !== window.top,
      signInPending: false,
      loginError: '',
      registrationError: '',
      registering: false,
      accountCreated: false,
    };
  },
  computed: {
    forgotPasswordUrl() {
      const brandName = this.$store.state.brandData.brandName.toLowerCase();

      return `https://app.${brandName}/#/forgotPassword`;
    },
  },
  beforeDestroy() {
    if (this.stopSignIn) this.stopSignIn();
  },
  methods: {
    signInEmbedded() {
      if (this.stopSignIn) this.stopSignIn();
      this.loginError = '';
      this.signInPending = true;
      try {
        this.stopSignIn = openEmbeddedSignIn(window,
          token => keycloakService.validateEmbeddedToken(token),
          user => {
            Cookies.remove('db71ab6b0872a8afd28afe1adf7de4b5', { path: '/' });
            setEmbeddedUser(user);
            this.$store.commit('login', user);
            this.signInPending = false;
            this.$emit('userLoggedIn');
          },
          message => { this.signInPending = false; this.loginError = message; }
        );
      } catch (_) {
        this.signInPending = false;
        this.loginError = 'We could not open sign-in. Please try again.';
      }
    },
    handleLoginTabChange(newTab) {
      if (this.stopSignIn) this.stopSignIn();
      this.signInPending = false;
      this.loginTab = newTab;
    },
    async login() {
      this.loginError = '';
      try {
        await keycloakService.login();
      } catch (_) {
        this.loginError = 'We could not start sign-in. Please try again.';
      }
    },
    async registerUser() {
      if (this.registering) return;
      if (this.accountCreated) return this.isEmbedded ? this.signInEmbedded() : this.login();
      this.$v.localSignupForm.$reset();

      this.$v.localSignupForm.$touch();

      if (this.$v.localSignupForm.$invalid) {
        return;
      }

      this.registrationError = '';
      this.registering = true;
      this.$loader.start('registerUser');

      const postParams = {
        first_name: this.localSignupForm.first_name,
        last_name: this.localSignupForm.last_name,
        company_name: this.localSignupForm.company_name,
        email: this.localSignupForm.email,
        username: this.localSignupForm.username,
        password: this.localSignupForm.password,
        password_confirmation: this.localSignupForm.password_confirmation,
        funnel_id: this.localSignupForm.funnel_id,
      };

      const gdJvCookie = Cookies.get(`gd_jv_f${this.localSignupForm.funnel_id}`);

      if (typeof gdJvCookie !== 'undefined') {
        postParams.gd_jv_cookie = gdJvCookie;
      }

      const gsJvCookie = Cookies.get(`gs_jv_f${this.localSignupForm.funnel_id}`);

      if (typeof gsJvCookie !== 'undefined') {
        postParams.gs_jv_cookie = gsJvCookie;
      }

      try {
        await axios.post(`/affiliate/${this.funnelId}/auth/register`, postParams);
        this.accountCreated = true;
        // Restore the existing account-creation -> login flow without leaving
        // the portal. A fresh client avoids any stale account interceptor.
        const { data: user } = await axios.create().post(`${window.config.apiCoreUrl}/auth/login`, {
          email: postParams.email, password: postParams.password,
        });
        if (!user || !user.id || typeof user.token !== 'string' || !user.token) throw new Error('Profile unavailable');
        if (this.isEmbedded) {
          Cookies.remove('db71ab6b0872a8afd28afe1adf7de4b5', { path: '/' });
          setEmbeddedUser(user);
        }
        else auth.setUserData(user, true);
        this.$store.commit('login', user);
        this.$emit('userLoggedIn');
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message;
        if (this.accountCreated) {
          this.loginTab = 'login';
          this.loginError = 'Your account was created. Please log in to finish connecting it.';
        } else {
          this.registrationError = message || 'We could not create your account. Please try again.';
        }
      } finally {
        this.localSignupForm.password = '';
        this.localSignupForm.password_confirmation = '';
        this.registering = false;
        this.$loader.end('registerUser');
      }
    },
    delayTouch($v) {
      $v.$reset();
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v));
      }
      touchMap.set($v, setTimeout($v.$touch, 1000));
    },
  },
  validations: {
    localSignupForm: {
      first_name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      last_name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      company_name: {
        maxLength: maxLength(30),
      },
      email: {
        required,
        email,
      },
      username: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(20),
        alphaNum,
        async isUnique(value) {
          if (value === '') {
            return true;
          }

          try {
            await axios.get(`/auth/username/${value}`);
            return true;
          } catch (error) {
            return false;
          }
        },
      },
      password: {
        required,
        minLength: minLength(8),
        goodPassword: (password) => {
          // a custom validator!
          return (
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password)
          );
        },
      },
      password_confirmation: {
        required,
        minLength: minLength(8),
        sameAsPassword: sameAs('password'),
      },
    },
  },
}
</script>

<style scoped>
.gs-portal-auth__open {
  display: inline-block;
  align-self: flex-start;
  padding: 12px 18px;
  border-radius: 10px;
  background: #f72585;
  color: white;
  font-weight: 700;
  text-decoration: none;
}
.gs-portal-auth {
  display: flex;
  justify-content: center;
  min-width: 0;
  padding: 28px 20px;
}

.gs-portal-auth__card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  max-width: 520px;
  min-width: 0;
  padding: 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-portal-auth__eyebrow {
  margin: 0 0 4px;
  color: var(--gs-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-portal-auth__title {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-portal-auth__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.gs-portal-auth__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.gs-portal-auth__error {
  margin: 0;
  color: var(--gs-danger);
  font-size: 13px;
}

.gs-portal-auth__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
}

.gs-portal-auth__link {
  padding: 4px 0;
  border: 0;
  background: transparent;
  font: inherit;
  text-decoration: underline;
  color: var(--gs-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.gs-portal-auth__switch { margin:0; padding-top:18px; border-top:1px solid var(--gs-border); color:var(--gs-muted-foreground); font-size:14px; }
.gs-portal-auth__open:disabled { opacity:.65; cursor:wait; }
.gs-portal-auth__open:focus-visible, .gs-portal-auth__link:focus-visible { outline:2px solid var(--gs-active); outline-offset:3px; }

@media (max-width: 560px) {
  .gs-portal-auth {
    padding: 16px 12px;
  }

  .gs-portal-auth__card {
    padding: 18px;
  }

  .gs-portal-auth__row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
